<template>
  <EditDialog
    v-model="dialog"
    :max-width="1280"
    :hide-buttons="true"
    :expandable="true"
    :min-content-height="640"
    content-class="TaskLogDialogShell"
    no-body-paddings
    @close="onClose()"
    test-id="taskLogDialog"
  >
    <template v-slot:title={}>
      <div class="TaskLogDialog__title">
        <div class="TaskLogDialog__mark">
          <ZpIcon :name="TEMPLATE_TYPE_ICONS[template?.type || ''] || 'terminal'" :size="20" />
        </div>
        <div class="TaskLogDialog__heading">
          <span class="zp-overline">{{ $t('taskRun') }}</span>
          <strong>
            {{ template ? template.name : $t('task', { expr: item ? item.id : null }) }}
          </strong>
          <router-link
            v-if="template"
            class="TaskLogDialog__template-link"
            :to="`/project/${projectId}/templates/${template.id}`"
            @click="close()"
          >
            {{ $t('template') }} {{ templateIdLabel }}
          </router-link>
        </div>
        <div class="TaskLogDialog__chips">
          <span class="TaskLogDialog__chip">
            <small>{{ $t('taskId') }}</small>
            <strong class="zp-tabular">{{ taskIdLabel }}</strong>
          </span>
          <span class="TaskLogDialog__chip">
            <small>{{ $t('application') }}</small>
            <strong>{{ appTitle }}</strong>
          </span>
          <span class="TaskLogDialog__chip">
            <small>{{ $t('status') }}</small>
            <TaskStatus v-if="item" :status="item.status" />
            <strong v-else>—</strong>
          </span>
        </div>
      </div>
    </template>
    <template v-slot:form="{}">

      <TaskLogView
        v-if="item != null"
        :project-id="projectId"
        :item="item"
        :template="template"
        :system-info="systemInfo"
      />

      <v-skeleton-loader
        class="task-log-view__placeholder"
        v-else
        type="
            table-heading,
            image,
            list-item-two-line,
            list-item-two-line,
            list-item-two-line,
            list-item-two-line,
            list-item-two-line,
            list-item-two-line,
            list-item-two-line,
            list-item-two-line"
      ></v-skeleton-loader>
    </template>
  </EditDialog>
</template>
<style lang="scss">
.TaskLogDialogShell {
  .v-card {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 120px);
    overflow: hidden;
    border: var(--zp-border-hair) solid var(--zp-border-default);
    border-radius: var(--zp-radius-md) !important;
    background: var(--zp-bg-surface) !important;
    color: var(--zp-text-primary);
  }

  .v-card__title {
    flex: 0 0 auto;
    min-height: 148px;
    margin-right: 0 !important;
    padding: var(--zp-space-lg) 112px var(--zp-space-lg) var(--zp-space-lg) !important;
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    white-space: normal !important;
    overflow: visible !important;
  }

  .v-card__text {
    display: flex;
    flex: 1 1 auto;
    min-height: 0 !important;
    overflow: hidden;
    background: var(--zp-bg-page);
  }

  .item-dialog__title-actions {
    top: var(--zp-space-lg);
    right: var(--zp-space-lg);
    background: transparent !important;
  }
}

.TaskLogDialog {
  &__title {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--zp-space-md);
    width: 100%;
    min-width: 0;
  }

  &__mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border: var(--zp-border-hair) solid var(--zp-border-default);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-page);
    color: var(--zp-brand);
  }

  &__heading {
    display: grid;
    gap: var(--zp-space-2xs);
    min-width: 0;

    strong {
      color: var(--zp-text-primary);
      font-family: var(--zp-font-display);
      font-size: var(--zp-text-xl);
      font-weight: 650;
      line-height: var(--zp-leading-tight);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__template-link {
    width: fit-content;
    color: var(--zp-brand) !important;
    font-size: var(--zp-text-sm);
    font-weight: 650;
    text-decoration: none;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--zp-space-sm);
    justify-content: flex-end;
  }

  &__chip {
    min-width: 132px;
    padding: var(--zp-space-xs) var(--zp-space-sm);
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-page);

    small,
    strong {
      display: block;
      line-height: var(--zp-leading-snug);
    }

    small {
      color: var(--zp-text-tertiary);
      font-size: var(--zp-text-2xs);
      font-weight: 700;
      letter-spacing: var(--zp-tracking-overline);
      text-transform: uppercase;
    }

    strong,
    .zp-status {
      margin-top: 2px;
    }
  }
}

.task-log-view__placeholder {
  margin-left: 24px;
  margin-right: 24px;
  height: calc(100dvh - 208px);
}

@media (max-width: 700px) {
  .TaskLogDialogShell {
    max-height: calc(100vh - 80px);
    margin: 16px !important;

    .v-card {
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 80px);
    }

    .v-card__title {
      flex: 0 0 auto;
      min-height: auto;
      padding: var(--zp-space-md) 56px var(--zp-space-md) var(--zp-space-md) !important;
    }

    .v-card__text {
      flex: 1 1 auto;
      min-height: 0 !important;
      overflow-y: auto;
    }
  }

  .TaskLogDialog {
    &__title {
      grid-template-columns: 44px minmax(0, 1fr);
    }

    &__chips {
      grid-column: 1 / -1;
      justify-content: stretch;
    }

    &__chip {
      flex: 1 1 104px;
      min-width: 0;
    }
  }
}
</style>
<script>
import TaskLogView from '@/components/TaskLogView.vue';
import EditDialog from '@/components/EditDialog.vue';
import ProjectMixin from '@/components/ProjectMixin';
import TaskStatus from '@/components/TaskStatus.vue';
import {
  APP_SHORT_TITLE,
  TEMPLATE_TYPE_ICONS,
} from '@/lib/constants';

export default {
  components: { EditDialog, TaskLogView, TaskStatus },

  mixins: [ProjectMixin],

  props: {
    value: Boolean,
    projectId: Number,
    itemId: Number,
    systemInfo: Object,
  },

  watch: {
    async dialog(val) {
      this.$emit('input', val);
    },

    async value(val) {
      this.item = null;
      this.template = null;
      this.dialog = val;
      await this.loadData();
    },

    async itemId() {
      await this.loadData();
    },
  },

  data() {
    return {
      item: null,
      dialog: null,
      template: null,
      TEMPLATE_TYPE_ICONS,
    };
  },

  computed: {
    taskIdLabel() {
      const id = this.item?.id || this.itemId;
      return id == null ? '—' : `#${String(id).padStart(4, '0')}`;
    },

    templateIdLabel() {
      const id = this.template?.id;
      return id == null ? '—' : `#${String(id).padStart(4, '0')}`;
    },

    appTitle() {
      return APP_SHORT_TITLE[this.template?.app] || this.template?.app || '—';
    },
  },

  methods: {
    close() {
      this.dialog = false;
      this.item = null;
      this.template = null;
      this.onClose();
    },

    async loadData() {
      if (this.itemId == null) {
        return;
      }
      this.item = await this.loadProjectResource('tasks', this.itemId);
      this.template = await this.loadProjectResource('templates', this.item.template_id);
    },

    onClose() {
      this.$emit('close');
    },
  },
};
</script>
